import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type AxiosError,
} from "axios";

interface ApiError {
  message: string;
  code?: string;
  details?: any;
}

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL,
      timeout: 30000, // 30 segundos de timeout
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor - adiciona JWT automaticamente
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("jwt");
        if (token && !config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Log requests em desenvolvimento
        if (import.meta.env.DEV) {
          console.log(
            `🚀 ${config.method?.toUpperCase()} ${config.url}`,
            config.data,
          );
        }

        return config;
      },
      (error) => {
        console.error("Request error:", error);
        return Promise.reject(error);
      },
    );

    // Response interceptor - trata erros globalmente
    this.client.interceptors.response.use(
      (response) => {
        // Log responses em desenvolvimento
        if (import.meta.env.DEV) {
          console.log(
            `✅ ${response.config.method?.toUpperCase()} ${response.config.url}`,
            response.data,
          );
        }
        return response;
      },
      (error: AxiosError<ApiError>) => {
        this.handleError(error);
        return Promise.reject(error);
      },
    );
  }

  private handleError(error: AxiosError<ApiError>): void {
    const status = error.response?.status;
    const message = error.response?.data?.message || error.message;

    // Verificar se é erro de timeout
    if (error.code === "ECONNABORTED") {
      console.error("Timeout na requisição");
      return;
    }

    switch (status) {
      case 401:
        // Token expirado ou inválido
        this.handleUnauthorized();
        break;
      case 403:
        console.error("Acesso negado");
        break;
      case 404:
        console.error("Recurso não encontrado");
        break;
      case 422:
        console.error("Dados inválidos");
        break;
      case 429:
        console.error("Muitas requisições. Tente novamente em alguns minutos.");
        break;
      case 500:
        console.error("Erro interno do servidor");
        break;
      default:
        if (message) {
          console.error(message);
        } else {
          console.error("Erro de conexão");
        }
    }
  }

  private handleUnauthorized(): void {
    // Limpar dados de autenticação
    localStorage.removeItem("jwt");

    // Redirecionar para login se não estiver já lá
    if (window.location.pathname !== "/login") {
      console.error("Sessão expirada. Faça login novamente.");
      window.location.href = "/login";
    }
  }

  // Métodos HTTP
  async get<T>(url: string, config?: any): Promise<AxiosResponse<T>> {
    return this.client.get<T>(url, config);
  }

  async post<T>(
    url: string,
    data?: any,
    config?: any,
  ): Promise<AxiosResponse<T>> {
    return this.client.post<T>(url, data, config);
  }

  async put<T>(
    url: string,
    data?: any,
    config?: any,
  ): Promise<AxiosResponse<T>> {
    return this.client.put<T>(url, data, config);
  }

  async patch<T>(
    url: string,
    data?: any,
    config?: any,
  ): Promise<AxiosResponse<T>> {
    return this.client.patch<T>(url, data, config);
  }

  async delete<T>(url: string, config?: any): Promise<AxiosResponse<T>> {
    return this.client.delete<T>(url, config);
  }

  // Método especial para Access Keys
  async postWithApiKey<T>(
    url: string,
    data?: any,
    apiKey?: string,
  ): Promise<AxiosResponse<T>> {
    const config = {
      headers: {
        "X-API-Key": apiKey || "",
      },
    };
    return this.client.post<T>(url, data, config);
  }
}

export const api = new ApiClient();
